import { ref, computed, watchEffect } from 'vue'
import en from '@/i18n/en'
import es from '@/i18n/es'
import fr from '@/i18n/fr'
import nl from '@/i18n/nl'
import de from '@/i18n/de'

export const LOCALES = ['en', 'es', 'fr', 'nl', 'de'] as const
export type Locale = (typeof LOCALES)[number]

// Open Graph locale per UI locale
const OG_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  nl: 'nl_NL',
  de: 'de_DE',
}

type Messages = Record<string, Record<string, string>>
const messages: Record<Locale, Messages> = { en: en as any, es: es as any, fr: fr as any, nl: nl as any, de: de as any }

// Only set when the user picks a language explicitly; otherwise we follow the browser
const STORAGE_KEY = 'bimdrop-locale'

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

function readStored(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLocale(stored) ? stored : null
  } catch {
    return null
  }
}

/** First supported language in the browser's preference list (fr-BE → fr, de-AT → de). */
function browserLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en'
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const tag of preferred) {
    const base = tag?.slice(0, 2).toLowerCase()
    if (isLocale(base)) return base
  }
  return 'en'
}

const userChoice = ref<Locale | null>(readStored())
const detected = ref<Locale>(browserLocale())
const locale = computed<Locale>(() => userChoice.value ?? detected.value)

if (typeof window !== 'undefined') {
  // Browser language changed while the app is open (only matters without a manual choice)
  window.addEventListener('languagechange', () => {
    detected.value = browserLocale()
  })
}

if (typeof document !== 'undefined') {
  watchEffect(() => {
    document.documentElement.lang = locale.value
    document.head.querySelector('meta[property="og:locale"]')?.setAttribute('content', OG_LOCALE[locale.value])
  })
}

export function useI18n() {
  const currentMessages = computed(() => messages[locale.value])

  function t(path: string, params?: Record<string, string | number>): string {
    const lookup = (msgs: Messages) => {
      let result: any = msgs
      for (const key of path.split('.')) result = result?.[key]
      return typeof result === 'string' ? result : undefined
    }
    // Fall back to English if a key is missing in the current locale
    const result = lookup(currentMessages.value) ?? lookup(messages.en)
    if (result === undefined) return path
    if (params) {
      return result.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? `{${k}}`))
    }
    return result
  }

  function setLocale(l: Locale) {
    userChoice.value = l
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {
      // storage blocked: choice lasts for this page view
    }
  }

  return { t, locale, setLocale }
}
